/** Parse Genesis agent-share notify payloads (BE `agent_share_notify`). */

export type AgentShareAction = 'granted' | 'changed' | 'revoked';

export type AgentShareNotification = {
  action: AgentShareAction;
  agentId?: string;
  agentName: string;
  permissionType?: string;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const VERB_TO_ACTION: Record<string, AgentShareAction> = {
  SHARE: 'granted',
  CHANGE: 'changed',
  REMOVE: 'revoked',
  GRANTED: 'granted',
  CHANGED: 'changed',
  REVOKED: 'revoked',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function parseJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isShareType(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toUpperCase();
  return normalized === 'AGENT_SHARE' || normalized === 'AGENTSHARE';
}

/** Pull the denormalized body out of a SignalR / store notification envelope. */
export function getDenormalizedNotificationPayload(notification: unknown): unknown {
  const root = asRecord(notification);
  if (!root) return null;

  const message = asRecord(root.message) ?? asRecord(root.Message) ?? root;
  const raw =
    message.denormalizedPayload ??
    message.DenormalizedPayload ??
    root.denormalizedPayload ??
    root.DenormalizedPayload;

  if (raw != null) {
    const parsed = parseJson(raw);
    return parsed ?? raw;
  }

  // Hub sometimes delivers the BE payload object directly.
  if (
    message.type === 'agent_share' ||
    isShareType(message.type) ||
    isShareType(asRecord(message.Message)?.type) ||
    isShareType(asRecord(message.message)?.type)
  ) {
    return message;
  }

  return null;
}

function actionFrom(value: unknown): AgentShareAction | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  return VERB_TO_ACTION[value.trim().toUpperCase()] ?? null;
}

function fromBePayload(payload: Record<string, unknown>): AgentShareNotification | null {
  const message = asRecord(payload.Message) ?? asRecord(payload.message);
  const type = payload.type ?? message?.type;
  if (type !== 'agent_share' && !isShareType(type) && !isShareType(message?.type)) {
    return null;
  }

  const action =
    actionFrom(payload.action) ??
    actionFrom(message?.Verb) ??
    actionFrom(message?.verb) ??
    'granted';
  const permission = asRecord(payload.permission);
  const agentName =
    (typeof message?.displayName === 'string' && message.displayName) ||
    (typeof payload.agentName === 'string' && payload.agentName) ||
    'an agent';
  const agentId =
    (typeof payload.agentId === 'string' && payload.agentId) ||
    (typeof payload.agent_id === 'string' && payload.agent_id) ||
    undefined;
  const permissionType =
    (typeof permission?.permissionType === 'string' && permission.permissionType) ||
    (typeof permission?.permission_type === 'string' && permission.permission_type) ||
    undefined;

  return { action, agentId, agentName, permissionType };
}

function fromActivity(source: Record<string, unknown>): AgentShareNotification | null {
  const activity = asRecord(source.activity);
  if (!activity) return null;
  const object = asRecord(activity.object);
  if (!isShareType(object?.type)) return null;

  const action = actionFrom(activity.verb) ?? 'granted';
  const agentName =
    (typeof object?.displayName === 'string' && object.displayName) || 'an agent';
  const agentId =
    (typeof object?.objectId === 'string' && object.objectId) ||
    (typeof object?.object_id === 'string' && object.object_id) ||
    undefined;

  return { action, agentId, agentName };
}

export function parseAgentShareNotification(
  source: unknown,
  depth = 0
): AgentShareNotification | null {
  if (depth > 4) return null;
  const parsed = parseJson(source);
  const record = asRecord(parsed);
  if (!record) return null;

  for (const key of ['denormalizedPayload', 'DenormalizedPayload'] as const) {
    if (record[key] != null && record[key] !== source) {
      const nested = parseAgentShareNotification(record[key], depth + 1);
      if (nested) return nested;
    }
  }

  const extra = asRecord(asRecord(record.activity)?.extraContext);
  if (extra) {
    const fromExtra = fromBePayload(extra) ?? parseAgentShareNotification(extra, depth + 1);
    if (fromExtra) return fromExtra;
  }

  return fromBePayload(record) ?? fromActivity(record);
}

export function agentSharePermissionLabel(
  permissionType: string | undefined,
  t: Translate
): string {
  if (permissionType === 'editor') {
    return String(t('SHARE_CAN_EDIT', { defaultValue: 'Can edit' }));
  }
  return String(t('SHARE_VIEW_ONLY', { defaultValue: 'View only' }));
}

export function agentShareTitle(share: AgentShareNotification, t: Translate): string {
  const keys = {
    granted: ['AGENT_SHARE_GRANTED_TITLE', 'Agent shared with you'],
    changed: ['AGENT_SHARE_CHANGED_TITLE', 'Agent sharing updated'],
    revoked: ['AGENT_SHARE_REVOKED_TITLE', 'Agent access removed'],
  } as const;
  const [key, fallback] = keys[share.action];
  return String(t(key, { defaultValue: fallback }));
}

export function agentShareDescription(
  share: AgentShareNotification,
  t: Translate
): string {
  const permission = agentSharePermissionLabel(share.permissionType, t);
  const agentName = share.agentName;
  if (share.action === 'granted') {
    return String(
      t('AGENT_SHARE_GRANTED_DESCRIPTION', {
        defaultValue: "You were given {{permission}} access to '{{agentName}}'.",
        permission,
        agentName,
      })
    );
  }
  if (share.action === 'changed') {
    return String(
      t('AGENT_SHARE_CHANGED_DESCRIPTION', {
        defaultValue: "Your access to '{{agentName}}' is now {{permission}}.",
        permission,
        agentName,
      })
    );
  }
  return String(
    t('AGENT_SHARE_REVOKED_DESCRIPTION', {
      defaultValue: "Your access to '{{agentName}}' was removed.",
      agentName,
    })
  );
}

export function agentDetailsPath(agentId: string): string {
  return `/agent-details/${agentId}`;
}

/**
 * Org-wide agent share reaches the sharer via OrganizationIds.
 * Callers should skip toast / UI for that viewer.
 */
export function isAgentShareFromCurrentUser(
  payload: unknown,
  currentUserId: string | null | undefined
): boolean {
  if (!currentUserId) return false;
  const record = asRecord(parseJson(payload));
  if (!record) return false;

  const message = asRecord(record.Message) ?? asRecord(record.message);
  const msgType = message?.type ?? record.type;
  if (!isShareType(msgType) && msgType !== 'agent_share') return false;

  const sharerId = record.sharerUserId ?? record.sharer_user_id;
  return typeof sharerId === 'string' && sharerId === currentUserId;
}

